import React from "react";
import { Link } from "react-router-dom";
import "../styles/LegalPage.css";

export default function PrivacyPage() {
  return (
    <div className="legal-root">
      <div className="legal-wrap">
        <header className="legal-header">
          <div className="legal-site-name">Azerodle</div>
          <h1 className="legal-title">Privacy Policy</h1>
          <div className="legal-meta">Effective Date: March 2026 · Last Updated: March 2026</div>
        </header>

        <section className="legal-section">
          <h2>1. Introduction</h2>
          <p>This Privacy Policy explains how Azerodle ("we", "us", "the Service") collects, uses, and protects your personal data when you use our website. We are committed to protecting your privacy and complying with the General Data Protection Regulation (GDPR) and applicable Polish law.</p>
          <div className="legal-highlight">
            Azerodle is operated by a private individual as a non-commercial hobby project based in Poland. It is not affiliated with Blizzard Entertainment, Inc.
          </div>
        </section>

        <section className="legal-section">
          <h2>2. Data We Collect</h2>
          <p>We collect only what is necessary to operate the Service:</p>
          <table className="legal-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Purpose</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Email address</td>
                <td>Account creation and authentication</td>
                <td>Provided by you</td>
              </tr>
              <tr>
                <td>Username &amp; chosen faction</td>
                <td>Display name and personalization</td>
                <td>Provided by you</td>
              </tr>
              <tr>
                <td>Game activity (guesses, scores, streaks)</td>
                <td>Core game functionality and leaderboards</td>
                <td>Generated by your use</td>
              </tr>
              <tr>
                <td>Account metadata (created date, last active)</td>
                <td>Account management and security</td>
                <td>Generated automatically</td>
              </tr>
            </tbody>
          </table>
          <p>We do not collect your real name, phone number, address, or payment information.</p>
        </section>

        <section className="legal-section">
          <h2>3. Legal Basis for Processing (GDPR)</h2>
          <p>We process your personal data on the following legal bases:</p>
          <ul className="legal-list">
            <li><strong>Contractual necessity</strong> — to provide you with the Service you signed up for (account, game data).</li>
            <li><strong>Consent</strong> — for optional features such as marketing communications, if and when introduced.</li>
            <li><strong>Legitimate interests</strong> — to maintain the security and integrity of the Service.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4. Third-Party Services</h2>
          <ul className="legal-list">
            <li><strong>Supabase</strong> — provides authentication and database storage. See <a href="https://supabase.com/privacy" target="_blank" rel="noreferrer">Supabase Privacy Policy</a>.</li>
            <li><strong>Advertising networks</strong> — may be added in the future. This policy will be updated and a cookie consent banner will appear before any tracking cookies are set.</li>
            <li><strong>Buy Me a Coffee</strong> — optional donations are handled entirely by the Buy Me a Coffee platform. See <a href="https://buymeacoffee.com/privacy-policy" target="_blank" rel="noreferrer">Buy Me a Coffee Privacy Policy</a>. We do not receive or store your payment details.</li>
            <li><strong>Google Search / SEO</strong> — when publicly indexed, Google may process anonymized traffic data.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>5. Cookies</h2>
          <p>Currently, the Service uses only functional cookies required for authentication (session tokens managed by Supabase). We do not use advertising or tracking cookies at this time.</p>
          <p>When advertising is added in the future, we will implement a cookie consent banner before any non-essential cookies are set.</p>
        </section>

        <section className="legal-section">
          <h2>6. Data Retention</h2>
          <p>We retain your personal data for as long as your account is active. If you request deletion of your account, we will remove your personal data within 30 days, except where retention is required by law.</p>
        </section>

        <section className="legal-section">
          <h2>7. Your Rights (GDPR)</h2>
          <ul className="legal-list">
            <li><strong>Right of access</strong> — request a copy of your personal data.</li>
            <li><strong>Right to rectification</strong> — request correction of inaccurate data.</li>
            <li><strong>Right to erasure</strong> — request deletion of your account and data ("right to be forgotten").</li>
            <li><strong>Right to restriction</strong> — request that we limit the processing of your data.</li>
            <li><strong>Right to data portability</strong> — receive your data in a structured, machine-readable format.</li>
            <li><strong>Right to object</strong> — object to processing based on legitimate interests.</li>
            <li><strong>Right to withdraw consent</strong> — where processing is based on consent, you may withdraw it at any time.</li>
          </ul>
          <p>To exercise any of these rights, contact us at <a href="mailto:contact@azerodle.com">contact@azerodle.com</a>. We will respond within 30 days.</p>
        </section>

        <section className="legal-section">
          <h2>8. Data Security</h2>
          <p>We take reasonable technical and organizational measures to protect your data, including secure HTTPS connections and delegating authentication to Supabase's managed infrastructure. However, no system is completely secure, and we cannot guarantee absolute security.</p>
        </section>

        <section className="legal-section">
          <h2>9. Children's Privacy</h2>
          <p>The Service is not directed at children under the age of 16. We do not knowingly collect personal data from children. If you believe a child has provided us with personal data, please contact us and we will delete it promptly.</p>
        </section>

        <section className="legal-section">
          <h2>10. International Transfers</h2>
          <p>Your data may be stored on servers outside of Poland or the EU (Supabase operates global infrastructure). Where this occurs, appropriate safeguards are in place in accordance with GDPR requirements.</p>
        </section>

        <section className="legal-section">
          <h2>11. Changes to This Policy</h2>
          <p>We may update this Privacy Policy from time to time. We will notify registered users of material changes by updating the "Last Updated" date. Continued use of the Service after changes constitutes acceptance of the revised Policy.</p>
        </section>

        <section className="legal-section">
          <h2>12. Contact &amp; Complaints</h2>
          <p>For any privacy-related questions or to exercise your rights, contact us at: <a href="mailto:contact@azerodle.com">contact@azerodle.com</a></p>
          <p>You also have the right to lodge a complaint with the Polish data protection authority: <a href="https://uodo.gov.pl" target="_blank" rel="noreferrer">UODO — uodo.gov.pl</a>.</p>
        </section>
      </div>
    </div>
  );
}